import React, { useState, useEffect, useContext, useRef } from 'react';
import { Col, Row } from 'reactstrap';
import { Box } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AppContext from '@contexts/AppContext';
import TableSortLabel from '@mui/material/TableSortLabel';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import Moment from 'moment';
import DeleteDocumentPopup from '@components/DeleteDocumentPopup';
import GridService from '@services/GridService';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  head: {
    backgroundColor: '#efefef',
    color: theme.palette.common.black,
  },
  body: {
    fontSize: '14px', // Set font size for the body cells only
  },
}));

const useStyles2 = {
  minWidth: 500,
};

const PartialPaymentsGrid = (props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [orderBy, setOrderBy] = useState('');
  const [orderDir, setOrderDir] = useState('ASC');
  const [rows, setRows] = useState([]);
  const { applicationId } = useParams();
  const navigate = useNavigate();

  //email form
  const toggle = () => setModal(!modal);
  const appContext = useContext(AppContext);
  const [modal, setModal] = useState(false);
  const [rowIndex, setRowIndex] = useState(undefined);
  const [givenRows, setGivenRows] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [pages, setPages] = useState(undefined);
  const [activeFieldForSorting, setActiveFieldForSorting] = useState();
  const mountedRef = useRef();

  const endpoint = `/ApplicationPartialPayment/`;

  useEffect(() => {
    if (!mountedRef.current) return;
    update();
  }, [page]);

  useEffect(() => {
    mountedRef.current = true;
    setPage(1);
  }, []);

  const update = () => {
    setIsLoading(true);

    GridService.search(
      endpoint,
      { applicationNo: applicationId },
      orderBy,
      orderDir,
      page,
      null,
      rowsPerPage,
    )
      .then((response) => {
        setRows(response.data.data);
        setPages(response.data.pages.slice(-1)[0]);
        setIsLoading(false);
      })
      .catch(() => {});
  };

  const deleteItem = () => {
    if (!(appContext?.userPermissions?.indexOf('Delete Payoffs') > -1)) {
      appContext.toastMessage.message = 'You do not have permissions to perform this action.';
      toast.error(appContext.toastMessage.message, { autoClose: 3000 });
      appContext.toastMessage.message = null;
      return;
    }

    GridService.delete(endpoint + givenRows[rowIndex].publicId)
      .then(() => {
        let items = [...givenRows];
        items.splice(rowIndex, 1);
        setRows(items);
        toggle();
        toast('Partial Payment has been successfully deleted.', { type: 'success' });
      })
      .catch((_) => {});
  };

  const arrange = (field) => {
    if (orderBy === field) {
      if (orderDir === 'ASC') {
        setOrderDir('DESC');
      } else {
        setOrderDir('ASC');
      }
    } else {
      setOrderBy(field);
      setOrderDir('ASC');
    }

    setPage(1);
  };

  const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const parseDollarString = (val) => {
    return val ? moneyFormatter.format(parseFloat(val).toFixed(2)) : '-';
    //return val? '$' + parseFloat(val).toFixed(2) : "-";
  };

  return (
    <>
      <Box m={5}></Box>
      <Row className="mb-5">
        <div className="col-md-6">
          <h3>Partial Payments</h3>
        </div>
        <div className="col-md-6">
          <a
            className="btn btn-primary mr-1 btn-sm  float-right"
            onClick={() =>
              navigate(
                `/underwriting/applications/${applicationId}/payoffs/partialpayments/new-partialpayment`,
              )
            }
          >
            New Partial Payment
          </a>
        </div>
      </Row>
      <Row>
        <Col applicationId={applicationId} md="12">
          <DeleteDocumentPopup modal={modal} toggle={toggle} deleteItem={deleteItem} />

          <TableContainer component={Paper}>
            <Table className={useStyles2} aria-label="custom pagination table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Id</StyledTableCell>

                  <StyledTableCell
                    onClick={() => {
                      arrange('partialPaymentDate');
                      setActiveFieldForSorting('partialPaymentDate');
                    }}
                  >
                    DATE
                    <TableSortLabel
                      active={activeFieldForSorting === 'partialPaymentDate' ? true : false}
                      direction={
                        activeFieldForSorting === 'partialPaymentDate'
                          ? orderDir.toLowerCase()
                          : 'asc'
                      }
                      onClick={() => arrange('partialPaymentDate')}
                    ></TableSortLabel>
                  </StyledTableCell>

                  <StyledTableCell
                    onClick={() => {
                      arrange('advanceno');
                      setActiveFieldForSorting('advanceno');
                    }}
                  >
                    ADVANCE NO
                    <TableSortLabel
                      active={activeFieldForSorting === 'advanceno' ? true : false}
                      direction={
                        activeFieldForSorting === 'advanceno' ? orderDir.toLowerCase() : 'asc'
                      }
                      onClick={() => arrange('advanceno')}
                    ></TableSortLabel>
                  </StyledTableCell>

                  <StyledTableCell
                    onClick={() => {
                      arrange('amountReceived');
                      setActiveFieldForSorting('amountReceived');
                    }}
                  >
                    AMOUNT RECEIVED
                    <TableSortLabel
                      active={activeFieldForSorting === 'amountReceived' ? true : false}
                      direction={
                        activeFieldForSorting === 'amountReceived' ? orderDir.toLowerCase() : 'asc'
                      }
                      onClick={() => arrange('amountReceived')}
                    ></TableSortLabel>
                  </StyledTableCell>

                  <StyledTableCell
                    onClick={() => {
                      arrange('checNo');
                      setActiveFieldForSorting('checNo');
                    }}
                  >
                    CHECK NO
                    <TableSortLabel
                      active={activeFieldForSorting === 'checNo' ? true : false}
                      direction={
                        activeFieldForSorting === 'checNo' ? orderDir.toLowerCase() : 'asc'
                      }
                      onClick={() => arrange('checNo')}
                    ></TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => {
                      arrange('notes');
                      setActiveFieldForSorting('notes');
                    }}
                  >
                    NOTES
                    <TableSortLabel
                      active={activeFieldForSorting === 'notes' ? true : false}
                      direction={activeFieldForSorting === 'notes' ? orderDir.toLowerCase() : 'asc'}
                      onClick={() => arrange('notes')}
                    ></TableSortLabel>
                  </StyledTableCell>

                  <StyledTableCell align="right">ACTIONS</StyledTableCell>
                </TableRow>
              </TableHead>
              {isLoading === false && (
                <TableBody>
                  {rows &&
                    rows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {row.id}
                        </TableCell>
                        <TableCell>
                          {row?.partialPaymentDate &&
                            Moment(row?.partialPaymentDate?.split('T')[0]).format(
                              'MM-DD-YYYY',
                            )}{' '}
                          {Moment(row?.partialPaymentDate).format('h:mm:ss A')}
                        </TableCell>
                        <TableCell>{row.advanceNo}</TableCell>
                        <TableCell>
                          {row.amountReceived !== null
                            ? parseDollarString(row.amountReceived)
                            : null}
                        </TableCell>
                        <TableCell>{row.checNo}</TableCell>
                        <TableCell>{row.notes}</TableCell>
                        <TableCell className="p-2" align="right">
                          <a
                            className="btn mr-1 btn-sm"
                            onClick={() =>
                              navigate(
                                `/underwriting/applications/${applicationId}/payoffs/partialpayments/${row.publicId}`,
                              )
                            }
                          >
                            <EditIcon />
                          </a>
                          <a
                            className="btn btn-sm"
                            onClick={() => {
                              setRowIndex(index);
                              setGivenRows(rows);
                              toggle();
                            }}
                          >
                            <DeleteIcon style={{ color: 'var(--red)' }} />
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              )}
              {isLoading && (
                <TableBody>
                  <TableRow>
                    <td></td>
                    <td>
                      <ReactLoading type="bars" color="#7E8299" />
                    </td>
                    <td></td>
                  </TableRow>
                </TableBody>
              )}

              <TableFooter></TableFooter>
            </Table>
          </TableContainer>
        </Col>
      </Row>
    </>
  );
};

export default PartialPaymentsGrid;
